// TaskFilters — Drawer с фильтрами по status / priority / assignee + кнопка "Сбросить".
// Используется в TasksListPage. Управляется controlled через value/onChange.
import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  type SelectChangeEvent,
} from '@mui/material';
import type { TaskFilters as TaskFiltersApi, TaskStatus, TaskPriority } from '@/api/tasks';

export interface TaskFiltersValue {
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeUserId?: string;
}

interface TaskFiltersProps {
  /** Текущие значения фильтров. */
  value: TaskFiltersValue;
  /** Колбэк при изменении фильтров. */
  onChange: (next: TaskFiltersValue) => void;
  /** Флаг открытия Drawer. */
  open?: boolean;
  /** Колбэк закрытия Drawer. */
  onClose?: () => void;
}

const STATUS_OPTIONS: TaskStatus[] = ['NEW', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELLED'];
const PRIORITY_OPTIONS: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/** Drawer с фильтрами задач (status / priority / assignee). */
export function TaskFilters({ value, onChange, open = false, onClose }: TaskFiltersProps): JSX.Element {
  const [draft, setDraft] = useState<TaskFiltersValue>(value);

  function update<K extends keyof TaskFiltersValue>(key: K, next: TaskFiltersValue[K]): void {
    const updated = { ...draft, [key]: next };
    setDraft(updated);
    onChange(updated);
  }

  function handleReset(): void {
    setDraft({});
    onChange({});
  }

  const content = (
    <Box sx={{ width: 320, p: 3 }} data-testid="task-filters">
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        Фильтры
      </Typography>
      <Stack spacing={2} divider={<Divider flexItem />}>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-status-label">Статус</InputLabel>
          <Select
            labelId="task-filter-status-label"
            label="Статус"
            value={draft.status ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('status', (v === '' ? undefined : v) as TaskStatus);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-priority-label">Приоритет</InputLabel>
          <Select
            labelId="task-filter-priority-label"
            label="Приоритет"
            value={draft.priority ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('priority', (v === '' ? undefined : v) as TaskPriority);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            {PRIORITY_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="task-filter-assignee-label">ID исполнителя</InputLabel>
          <Select
            labelId="task-filter-assignee-label"
            label="ID исполнителя"
            value={draft.assigneeUserId ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value as string;
              update('assigneeUserId', v === '' ? undefined : v);
            }}
          >
            <MenuItem value="">Любой</MenuItem>
            <MenuItem value="me">Я</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={handleReset} fullWidth>
          Сбросить
        </Button>
        {onClose ? (
          <Button variant="contained" onClick={onClose} fullWidth>
            Применить
          </Button>
        ) : null}
      </Box>
    </Box>
  );

  if (open !== undefined && onClose) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        {content}
      </Drawer>
    );
  }
  return content;
}

// Подавляем unused-import для алиаса TaskFiltersApi (зарезервировано для batch-режима).
export type { TaskFiltersApi };
